import * as React from 'react'
import { connect } from 'react-redux'
import type { Dispatch } from 'redux'
import styled from 'styled-components'
import HtmlHead from '@components/HtmlHead'
import type { AppStateType } from '../../reducers/appstate'
import { withRouter } from 'next/router'
import { GlobalStyle } from '@components/shared'
import { colors } from '@components/shared/colors'
import TopNav from '@components/TopNav'
import Footer from '@components/Footer'
import type { PurchaseGiftParamsType } from '../../models/Gift'
import { purchaseGiftAndGetCode } from '../../actions/giftActions'
import GiftCheckout from '@components/gift/GiftCheckout'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements
} from '@stripe/react-stripe-js'
import { envConfig } from '../../env'
import TopNavMobile from '@components/TopNavMobile'
import BackgroundGradient from '@components/BackgroundGradient'

type PropsType = {
  purchasingGiftErrorMessage?: string,
  purchasingGift: boolean,
  giftCode?: string,
  newPrice?: string,
  promoText?: string,
  featureValentinesGift: boolean,
  featurePrideGift: boolean
}
type DispatchPropsType = {
  completeOrder: (params: PurchaseGiftParamsType, name: string, stripe: any) => void
}

function Checkout (props: PropsType & DispatchPropsType): React.ReactNode {
  const [stripePromise, setStripePromise] = React.useState<any>(null)
  const [isClient, setIsClient] = React.useState(false)

  React.useEffect((): void => {
    // Only load Stripe on client side to prevent hydration mismatch
    setIsClient(true)
    const stripeKey = envConfig().STRIPE_KEY
    if (stripeKey) {
      loadStripe(stripeKey)
        .then((stripe: any): void => {
          setStripePromise(stripe)
        })
        .catch((error: Error): void => {
          console.error('Failed to load Stripe:', error)
          // Report to Bugsnag if available
          if (window.Bugsnag) {
            window.Bugsnag.notify(error, (event: any): void => {
              event.addMetadata('context', {
                component: 'GiftCheckout',
                action: 'loadStripe'
              })
            })
          }
        })
    }
  }, [])
  let htmlTitle = 'Dipsea | The Perfectly Sexy Gift'
  const htmlDescription = 'Gift a loved one a year of sexy audio stories, intimate wellness sessions, and sleep scenes.'
  if (props.featurePrideGift) {
    htmlTitle = 'Dipsea | The Perfectly Sexy Gift For Pride'
  } else if (props.featureValentinesGift) {
    htmlTitle = 'Dipsea | The Perfectly Sexy Valentines Day Gift'
  }

  return (
    <>
      <Container>
        <BackgroundGradient
          quadrant={1}
          gradientColor={colors.midnightBlue}
          gradientPosition='bottom'
          parallax
        />
      </Container>
      <HtmlHead
        title={htmlTitle}
        description={htmlDescription}
      />
      <GlobalStyle backgroundColor={colors.midnightBlue} />
      <TopNav backgroundColor={colors.midnightBlue95} isAssetLoaded={true} />
      <TopNavMobile backgroundColor={colors.midnightBlue95} isAssetLoaded={true} />
      {!isClient
        ? (
          // Show loading state during SSR and initial client render
          <div style={{ padding: '40px', textAlign: 'center', color: colors.white }}>
            <h2>Loading payment form...</h2>
          </div>
          )
        : stripePromise
          ? (
            <Elements stripe={stripePromise}>
              <GiftCheckout
                purchasingGift={props.purchasingGift}
                completeOrder={props.completeOrder}
                purchasingGiftErrorMessage={props.purchasingGiftErrorMessage}
                giftCode={props.giftCode}
                newPrice={props.newPrice}
                promoText={props.promoText}
              />
            </Elements>
            )
          : (
            <div style={{ padding: '40px', textAlign: 'center', color: colors.white }}>
              <h2>Payment processing is temporarily unavailable</h2>
              <p>Please try again later or contact support.</p>
            </div>
            )}
      <Footer backgroundColor={colors.midnightBlue} />

    </>
  )
}

const mapStateToProps = (state: AppStateType): { purchasingGift: any, giftCode: any, purchasingGiftErrorMessage: any, newPrice: any, promoText: any, featureValentinesGift: any, featurePrideGift: any } => {
  return {
    purchasingGift: state.purchasingGift,
    giftCode: state.giftCode,
    purchasingGiftErrorMessage: state.purchasingGiftErrorMessage,
    newPrice: (state.subscriptionText?.promoText && !state.subscriptionText?.promoError) ? (state.subscriptionText || {}).price : undefined,
    promoText: (state.subscriptionText?.promoText && !state.subscriptionText?.promoError) ? (state.subscriptionText || {}).promoText : undefined,
    featureValentinesGift: state.featureValentinesGift,
    featurePrideGift: state.featurePrideGift
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchPropsType => {
  return {
    completeOrder: (params: PurchaseGiftParamsType, name: string, stripe: any, cardElement?: any): void => dispatch(purchaseGiftAndGetCode(params, name, stripe, cardElement))
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Checkout) as any) as any

const Container = styled.div<any>`
  height: 100vh;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`
